import React, { useState } from "react"
import { useMutation, useQuery } from "urql"

import ArchiveIntakeFormSubmissionModal from "../../components/shared/ArchiveIntakeFormSubmissionModal"
import IntakeFormListSection from "../../components/shared/IntakeFormListSection"
import IntakeFormPdfActions from "../../components/shared/IntakeFormPdfActions"
import IntakeFormSelectModal, { MODE_SEND } from "../../components/shared/IntakeFormSelectModal"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { ToastProvider, useToast } from "../../contexts/ToastContext"
import useIsClient from "../../hooks/useIsClient"
import GraphQLProvider from "../../providers/GraphQLProvider"
import { ARCHIVE_INTAKE_FORM_SUBMISSION_MUTATION } from "../../utils/intakeFormSubmissionMutations"

import ClientPageView from "./ClientPageView"

const CLIENT_INTAKE_FORM_QUERY = `
  query IntakeForm($clientId: ID!, $intakeFormId: ID) {
    client(clientId: $clientId) {
      id
      intakeForm(intakeFormId: $intakeFormId) { text time }
      intakeFormSchema(intakeFormId: $intakeFormId)
      intakeFormSubmitted(intakeFormId: $intakeFormId)
      intakeFormHasDataIssue(intakeFormId: $intakeFormId)
      intakeLastSentAt
    }
  }
`

const CLIENT_INTAKE_SUBMISSIONS_QUERY = `
  query ClientIntakeSubmissionsList($clientId: ID!) {
    client(clientId: $clientId) {
      id
      intakeSubmissions {
        intakeFormId
        intakeFormName
        status
        completedAt
      }
    }
  }
`

const SEND_INTAKE_FORMS_MUTATION = `
  mutation SendIntakeFormsClientPage($clientId: ID!, $intakeFormIds: [ID!]!) {
    sendIntakeForms(clientId: $clientId, intakeFormIds: $intakeFormIds) {
      result
      errors
    }
  }
`

const schemaPages = (schema) => (Array.isArray(schema?.pages) ? schema.pages : [])
const elementsOnPage = (page) => (Array.isArray(page?.elements) ? page.elements : [])
const elementsInPanel = (panel) => (Array.isArray(panel?.elements) ? panel.elements : [])

function IntakeFormListView({ client, upcomingAppointments, clientNotesEnabled, multipleIntakeFormsEnabled }) {
  const [intakeSelectModalOpen, setIntakeSelectModalOpen] = useState(false)
  const [archiveTargetForm, setArchiveTargetForm] = useState(null)
  const { showToast } = useToast()

  const [{ data }, reexecuteQuery] = useQuery({
    query: CLIENT_INTAKE_SUBMISSIONS_QUERY,
    variables: { clientId: client.id },
    requestPolicy: "network-only"
  })

  const [, sendIntakeForms] = useMutation(SEND_INTAKE_FORMS_MUTATION)
  const [, archiveIntakeFormSubmission] = useMutation(ARCHIVE_INTAKE_FORM_SUBMISSION_MUTATION)

  const intakeSubmissions = data?.client?.intakeSubmissions ?? []
  const completedFormIds = intakeSubmissions.filter((s) => s.status === "completed").map((s) => s.intakeFormId)

  const handleSendForms = (selectedForms) => {
    const formIds =
      Array.isArray(selectedForms) && selectedForms.length > 0 && typeof selectedForms[0] === "object"
        ? selectedForms.map((f) => f.id)
        : selectedForms
    sendIntakeForms({ clientId: client.id, intakeFormIds: formIds }).then((result) => {
      if (result.error) {
        showToast({
          content: multipleIntakeFormsEnabled !== false ? "Error sending intake forms" : "Error sending intake form",
          type: "error"
        })
        return
      }
      const payload = result.data?.sendIntakeForms
      if (payload?.result === "error") {
        showToast({ content: payload.errors?.join(", ") ?? "Error", type: "error" })
        return
      }
      showToast(multipleIntakeFormsEnabled !== false ? "Intake forms sent" : "Intake form sent")
      reexecuteQuery({ requestPolicy: "network-only" })
    })
  }

  const handleFormAction = (actionId, form) => {
    if (actionId === "view" && form?.id) {
      window.location.href = `/portal/clients/${client.id}/intake_form?formId=${form.id}`
      return
    }
    if (actionId === "resend") {
      handleSendForms([form.id])
      return
    }
    if (actionId === "remove") {
      setArchiveTargetForm(form)
    }
  }

  const handleArchiveConfirm = async () => {
    if (!archiveTargetForm?.id) return

    const result = await archiveIntakeFormSubmission({
      clientId: client.id,
      intakeFormId: archiveTargetForm.id
    })

    if (result.error) {
      showToast({ content: "Error deleting intake response", type: "error" })
      return
    }

    const payload = result.data?.archiveIntakeFormSubmission
    if (payload?.result === "error") {
      showToast({ content: payload.errors?.join(", ") ?? "Error deleting intake response", type: "error" })
      return
    }

    showToast("Intake response deleted")
    setArchiveTargetForm(null)
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return (
    <ClientPageView
      client={client}
      upcomingAppointments={upcomingAppointments}
      clientNotesEnabled={clientNotesEnabled}
      multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
      path={`/portal/clients/${client.id}/intake_form`}>
      <IntakeFormListSection
        forms={intakeSubmissions.map((s) => ({
          id: s.intakeFormId,
          name: s.intakeFormName,
          status: s.status,
          completedAt: s.completedAt
        }))}
        count={intakeSubmissions.length}
        actionLabel={multipleIntakeFormsEnabled !== false ? "Send a form" : "Send form"}
        onAction={() => setIntakeSelectModalOpen(true)}
        onFormAction={handleFormAction}
        multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
        showActionButton={multipleIntakeFormsEnabled !== false}
        actionIdsByStatus={{
          not_sent: [],
          sent: ["view", "resend"],
          completed: ["view", "remove"]
        }}
      />
      <ArchiveIntakeFormSubmissionModal
        visible={!!archiveTargetForm}
        formName={archiveTargetForm?.name}
        onClose={() => setArchiveTargetForm(null)}
        onConfirm={handleArchiveConfirm}
      />
      <IntakeFormSelectModal
        visible={intakeSelectModalOpen}
        onClose={() => setIntakeSelectModalOpen(false)}
        onSubmit={handleSendForms}
        completedFormIds={completedFormIds}
        mode={MODE_SEND}
      />
    </ClientPageView>
  )
}

const IntakeForm = ({
  client: clientProp,
  upcomingAppointments,
  clientNotesEnabled,
  multipleIntakeFormsEnabled,
  surveyJsLicenseKey,
  formId: formIdProp
}) => {
  const [client] = useState(clientProp)
  const [isConsentFieldsExpanded, setIsConsentFieldsExpanded] = useState(false)
  const { showToast } = useToast()
  const isClient = useIsClient()
  const formId = formIdProp || (isClient ? new URLSearchParams(window.location.search).get("formId") : null)

  const [{ data, fetching }] = useQuery({
    query: CLIENT_INTAKE_FORM_QUERY,
    variables: { clientId: client.id, intakeFormId: formId || null },
    requestPolicy: "network-only",
    pause: !formId
  })

  if (!formId) {
    return (
      <IntakeFormListView
        client={client}
        upcomingAppointments={upcomingAppointments}
        clientNotesEnabled={clientNotesEnabled}
        multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
      />
    )
  }

  const rawJson = data?.client?.intakeForm?.text
  const parsedJson = rawJson && JSON.parse(rawJson)
  const formSchema = data?.client?.intakeFormSchema && JSON.parse(data.client.intakeFormSchema || "null")

  if (!isClient || fetching) {
    return (
      <ClientPageView
        client={client}
        upcomingAppointments={upcomingAppointments}
        clientNotesEnabled={clientNotesEnabled}
        multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
        path={`/portal/clients/${client.id}/intake_form`}>
        <LoadingSpinner />
      </ClientPageView>
    )
  }

  // Helper function to find field type in the schema
  const getFieldType = (fieldName) => {
    if (!formSchema?.pages) return null

    for (const page of schemaPages(formSchema)) {
      for (const element of elementsOnPage(page)) {
        if (element.type === "panel") {
          for (const panelElement of elementsInPanel(element)) {
            if (panelElement.name === fieldName || panelElement.title === fieldName) {
              return panelElement.type
            }
          }
        } else if (element.name === fieldName || element.title === fieldName) {
          return element.type
        }
      }
    }
    return null
  }

  // Helper function to get field configuration from schema
  const getFieldConfig = (fieldName) => {
    if (!formSchema?.pages) return null

    for (const page of schemaPages(formSchema)) {
      for (const element of elementsOnPage(page)) {
        if (element.type === "panel") {
          for (const panelElement of elementsInPanel(element)) {
            if (panelElement.name === fieldName || panelElement.title === fieldName) {
              return panelElement
            }
          }
        } else if (element.name === fieldName || element.title === fieldName) {
          return element
        }
      }
    }
    return null
  }

  // Helper function to render field value based on type
  const renderFieldValue = (fieldName, value) => {
    const fieldType = getFieldType(fieldName)
    const fieldConfig = getFieldConfig(fieldName)

    // Handle ranking type (used for disclaimers and consents)
    if (fieldType === "ranking" && Array.isArray(value)) {
      const allChoices = fieldConfig?.choices || []
      const hasUnrankedItems = allChoices.length > value.length

      return (
        <div className="ml-4">
          <div className="mb-2">
            <span className="text-sm font-semibold text-gray-700">Client&apos;s Priority Order:</span>
          </div>
          <ol className="list-decimal space-y-1 pl-5">
            {value.map((item, index) => (
              <li key={index} className="text-gray-900">
                {item}
              </li>
            ))}
          </ol>
          {hasUnrankedItems && (
            <div className="mt-3">
              <span className="text-sm font-semibold text-gray-600">Not Ranked:</span>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-600">
                {allChoices
                  .filter((choice) => !value.includes(choice))
                  .map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
              </ul>
            </div>
          )}
          {allChoices.length > 0 && (
            <div className="mt-3 text-xs text-gray-500">
              Total items: {value.length} ranked out of {allChoices.length}
            </div>
          )}
        </div>
      )
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return (
        <ul className="list-inside list-disc">
          {value.map((item, index) =>
            item != null && typeof item === "object" ? (
              <li key={index}>
                Row {index + 1}:
                <ul className="ml-4 list-inside list-disc">
                  {Object.entries(item).map(([subKey, subValue]) => (
                    <li key={subKey}>
                      {subKey}: {subValue}
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={index}>{item}</li>
            )
          )}
        </ul>
      )
    }

    // Handle objects (exclude null — typeof null === "object")
    if (value != null && typeof value === "object" && !Array.isArray(value)) {
      return (
        <div>
          {Object.entries(value).map(([key, subValue]) => (
            <li key={key}>
              {key}:{" "}
              {subValue != null && typeof subValue === "object" && !Array.isArray(subValue) ? (
                <ul className="ml-4 list-inside list-disc">
                  {Object.entries(subValue).map(([subKey, subSubValue]) => (
                    <li key={subKey}>
                      {subKey}: {subSubValue}
                    </li>
                  ))}
                </ul>
              ) : (
                subValue
              )}
            </li>
          ))}
        </div>
      )
    }

    // Handle images
    if (typeof value === "string" && value.startsWith("data:image")) {
      return <img src={value} alt="intake form image" className="max-h-[200px] max-w-[200px]" />
    }

    // Handle booleans and strings
    return <span>{value === true ? "Yes" : value === false ? "No" : value}</span>
  }

  // Helper function to get all ranking fields from schema
  const getRankingFieldsFromSchema = () => {
    if (!formSchema?.pages) return []

    const rankingFields = []
    for (const page of schemaPages(formSchema)) {
      for (const element of elementsOnPage(page)) {
        if (element.type === "panel") {
          for (const panelElement of elementsInPanel(element)) {
            if (panelElement.type === "ranking") {
              rankingFields.push({
                name: panelElement.name || panelElement.title,
                title: panelElement.title,
                choices: panelElement.choices || [],
                description: panelElement.description
              })
            }
          }
        } else if (element.type === "ranking") {
          rankingFields.push({
            name: element.name || element.title,
            title: element.title,
            choices: element.choices || [],
            description: element.description
          })
        }
      }
    }
    return rankingFields
  }

  const rankingFieldsInTemplate = getRankingFieldsFromSchema()
  const intakeFormName = formSchema?.title || "Intake form"

  const intakeFormLabel = multipleIntakeFormsEnabled !== false ? "intake forms" : "intake form"

  return (
    <ClientPageView
      client={client}
      upcomingAppointments={upcomingAppointments}
      clientNotesEnabled={clientNotesEnabled}
      multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
      path={`/portal/clients/${client.id}/intake_form`}>
      <div className="mb-4">
        <a href={`/portal/clients/${client.id}/intake_form`} className="font-bold text-teal hover:underline">
          ← Back to {intakeFormLabel}
        </a>
      </div>
      {data?.client?.intakeFormHasDataIssue ? (
        <div className="rounded-md bg-yellow-100 p-4">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="size-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Data Issue Detected</h3>
              <div className="mt-2 text-sm text-yellow-800">
                <p>This client submitted an intake form, but there is an issue accessing the data.</p>
                <p className="mt-2">Please contact support if you need to recover this information.</p>
              </div>
            </div>
          </div>
        </div>
      ) : parsedJson ? (
        <div className="mb-6">
          {/* Show ranking fields template info if they exist */}
          {rankingFieldsInTemplate.length > 0 && (
            <div className="mb-6 rounded-lg border border-blue-300">
              <button
                onClick={() => setIsConsentFieldsExpanded(!isConsentFieldsExpanded)}
                className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-blue-50">
                <div>
                  <h3 className="text-lg font-semibold text-blue-800">📋 Consent Fields in This Form</h3>
                  <p className="mt-1 text-sm text-blue-700">
                    Your intake form includes {rankingFieldsInTemplate.length} field(s) for consent and priority
                    assessment
                  </p>
                </div>
                <svg
                  className={`size-5 shrink-0 text-blue-800 transition-transform ${
                    isConsentFieldsExpanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isConsentFieldsExpanded && (
                <div className="border-t border-blue-300 p-4 pt-3">
                  {rankingFieldsInTemplate.map((field, idx) => {
                    // Helper function to format text with line breaks and bullets
                    const formatText = (text) => {
                      if (!text) return null
                      // Split by line breaks
                      const lines = text.split(/\n|\\n/)
                      return lines.map((line, i) => {
                        // Check if line starts with bullet point
                        if (line.trim().startsWith("•") || line.trim().startsWith("*")) {
                          return (
                            <div key={i} className="mb-1 flex">
                              <span className="mr-2">•</span>
                              <span>{line.replace(/^[•*]\s*/, "").trim()}</span>
                            </div>
                          )
                        }
                        // Regular line with line break
                        return line.trim() ? (
                          <div key={i} className="mb-2">
                            {line.trim()}
                          </div>
                        ) : null
                      })
                    }

                    return (
                      <div key={idx} className="mb-4 rounded border border-blue-300 bg-white p-3 last:mb-0">
                        <div className="mb-2">
                          {formatText(field.title) || <h4 className="font-semibold text-gray-900">{field.title}</h4>}
                        </div>
                        {field.description && (
                          <div className="mb-2 text-sm text-gray-600">{formatText(field.description)}</div>
                        )}
                        <div className="text-sm text-gray-700">
                          {field.choices.length > 0 && (
                            <span className="font-medium">Available options ({field.choices.length}):</span>
                          )}
                          <ul className="mt-1 list-inside list-disc pl-2">
                            {field.choices.map((choice, i) => (
                              <li key={i} className="text-gray-600">
                                {choice}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Show client responses */}
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Client&apos;s Responses</h3>
            {formSchema && (
              <IntakeFormPdfActions
                surveyJson={formSchema}
                responseData={parsedJson}
                formName={`${client.name}-${intakeFormName}`}
                licenseKey={surveyJsLicenseKey}
                onError={(error) =>
                  showToast({
                    content: error?.message || "Unable to export this form.",
                    type: "error"
                  })
                }
              />
            )}
          </div>
          {Object.entries(parsedJson).map(([fieldName, value]) => (
            <div key={fieldName} className="mb-2 flex flex-col">
              <span className="font-bold">{fieldName.replace("-Comment", " (Comment)")}</span>
              {renderFieldValue(fieldName, value)}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-dark">
          {data?.client?.intakeLastSentAt
            ? "This client has not submitted this form yet."
            : "This client has not been sent this form yet."}
        </p>
      )}
    </ClientPageView>
  )
}

export default function ClientIntakeForm(props) {
  return (
    <ToastProvider>
      <GraphQLProvider>
        <IntakeForm {...props} />
      </GraphQLProvider>
    </ToastProvider>
  )
}
